import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { GameStatus } from '../../jscaip/GameStatus';
import { Ordinal } from '../../jscaip/Ordinal';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { LinesOfActionFailure } from './LinesOfActionFailure';
import { LinesOfActionMove } from './LinesOfActionMove';
import { LinesOfActionState } from './LinesOfActionState';
export class LinesOfActionNode extends GameNode {
}
export class LinesOfActionRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (LinesOfActionRules.singleton.isAbsent()) {
            LinesOfActionRules.singleton = MGPOptional.of(new LinesOfActionRules());
        }
        return LinesOfActionRules.singleton.get();
    }
    getInitialState() {
        const _ = PlayerOrNone.NONE;
        const O = PlayerOrNone.ZERO;
        const X = PlayerOrNone.ONE;
        const board = [
            [_, O, O, O, O, O, O, _],
            [X, _, _, _, _, _, _, X],
            [X, _, _, _, _, _, _, X],
            [X, _, _, _, _, _, _, X],
            [X, _, _, _, _, _, _, X],
            [X, _, _, _, _, _, _, X],
            [X, _, _, _, _, _, _, X],
            [_, O, O, O, O, O, O, _],
        ];
        return new LinesOfActionState(board, 0);
    }
    static getNumberOfGroups(state) {
        const groups = TableUtils.create(LinesOfActionState.SIZE, LinesOfActionState.SIZE, -1);
        const numGroups = PlayerNumberMap.of(0, 0);
        let highestGroup = 0;
        for (const coordAndContent of state.getCoordsAndContents()) {
            if (groups[coordAndContent.coord.y][coordAndContent.coord.x] === -1) {
                if (coordAndContent.content.isPlayer()) {
                    highestGroup += 1;
                    LinesOfActionRules.markGroupStartingAt(state, groups, coordAndContent.coord, highestGroup);
                    numGroups.add(coordAndContent.content, 1);
                }
            }
        }
        return numGroups;
    }
    static markGroupStartingAt(state, groups, pos, id) {
        const stack = [pos];
        const player = state.getPieceAt(pos);
        while (stack.length > 0) {
            const coord = Utils.getNonNullable(stack.pop());
            if (groups[coord.y][coord.x] === -1) {
                const content = state.getPieceAt(coord);
                if (content === player) {
                    groups[coord.y][coord.x] = id;
                    for (const dir of Ordinal.ORDINALS) {
                        const next = coord.getNext(dir);
                        if (state.isOnBoard(next)) {
                            stack.push(next);
                        }
                    }
                }
                else if (content.isNone()) {
                    groups[coord.y][coord.x] = 0;
                }
            }
        }
    }
    applyLegalMove(move, state, _config, _info) {
        const board = state.getCopiedBoard();
        board[move.getStart().y][move.getStart().x] = PlayerOrNone.NONE;
        board[move.getEnd().y][move.getEnd().x] = state.getCurrentPlayer();
        return new LinesOfActionState(board, state.turn + 1);
    }
    static isLegal(move, state) {
        const piece = state.getPieceAt(move.getStart());
        if (piece.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (piece === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        if (move.getDistance() !== this.numberOfPiecesOnLine(state, move.getStart(), move.direction)) {
            return MGPValidation.failure(LinesOfActionFailure.INVALID_MOVE_LENGTH());
        }
        if (move.getJumpedOverCoords().some((c) => state.getPieceAt(c) === state.getCurrentOpponent())) {
            return MGPValidation.failure(LinesOfActionFailure.CANNOT_JUMP_OVER_OPPONENT());
        }
        if (state.getPieceAt(move.getEnd()) === state.getCurrentPlayer()) {
            return MGPValidation.failure(RulesFailure.SHOULD_LAND_ON_EMPTY_OR_OPPONENT_SPACE());
        }
        return MGPValidation.SUCCESS;
    }
    isLegal(move, state) {
        return LinesOfActionRules.isLegal(move, state);
    }
    static numberOfPiecesOnLine(state, pos, dir) {
        let count = 0;
        for (const coord of LinesOfActionRules.getLineCoords(pos, dir)) {
            if (state.getPieceAt(coord).isPlayer()) {
                count += 1;
            }
        }
        return count;
    }
    static getLineCoords(pos, dir) {
        const entranceAndDir = LinesOfActionRules.getEntranceAndForwardDirection(pos, dir);
        let current = entranceAndDir[0];
        const forwardDirection = entranceAndDir[1];
        const coords = [];
        while (LinesOfActionState.isOnBoard(current)) {
            coords.push(current);
            current = current.getNext(forwardDirection);
        }
        return coords;
    }
    static getEntranceAndForwardDirection(pos, dir) {
        switch (dir) {
            case Ordinal.UP:
            case Ordinal.DOWN:
                return [new Coord(pos.x, 0), Ordinal.DOWN];
            case Ordinal.LEFT:
            case Ordinal.RIGHT:
                return [new Coord(0, pos.y), Ordinal.RIGHT];
            case Ordinal.UP_RIGHT:
            case Ordinal.DOWN_LEFT:
                return [new Coord(Math.max(0, (pos.x + pos.y) - 7), Math.min(7, pos.x + pos.y)), Ordinal.UP_RIGHT];
            default:
                Utils.expectToBeMultiple(dir, [Ordinal.UP_LEFT, Ordinal.DOWN_RIGHT]);
                return [
                    new Coord(pos.x - Math.min(pos.x, pos.y), pos.y - Math.min(pos.x, pos.y)),
                    Ordinal.DOWN_RIGHT,
                ];
        }
    }
    static getVictory(state) {
        // Returns a player in case of victory (NONE if it is a draw), otherwise an empty optional
        const groups = LinesOfActionRules.getNumberOfGroups(state);
        const groupsZero = groups.get(Player.ZERO);
        const groupsOne = groups.get(Player.ONE);
        if (groupsZero === 1 && groupsOne === 1) {
            return MGPOptional.of(PlayerOrNone.NONE);
        }
        else if (groupsZero === 1) {
            return MGPOptional.of(Player.ZERO);
        }
        else if (groupsOne === 1) {
            return MGPOptional.of(Player.ONE);
        }
        else {
            return MGPOptional.empty();
        }
    }
    static possibleTargets(state, start) {
        let targets = new CoordSet();
        for (const dir of Ordinal.ORDINALS) {
            const numberOfPiecesOnLine = LinesOfActionRules.numberOfPiecesOnLine(state, start, dir);
            const target = start.getNext(dir, numberOfPiecesOnLine);
            if (state.isOnBoard(target)) {
                const move = LinesOfActionMove.from(start, target).get();
                const legality = LinesOfActionRules.isLegal(move, state);
                if (legality.isSuccess()) {
                    targets = targets.addElement(target);
                }
            }
        }
        return targets;
    }
    getGameStatus(node) {
        const state = node.gameState;
        const groups = LinesOfActionRules.getNumberOfGroups(state);
        const zero = groups.get(Player.ZERO);
        const one = groups.get(Player.ONE);
        if (zero === 1 && one > 1) {
            return GameStatus.ZERO_WON;
        }
        else if (zero > 1 && one === 1) {
            return GameStatus.ONE_WON;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
}
